import React, { useState, useEffect, useRef } from 'react';
import {
  listarMotos, inserirMoto, atualizarMoto, excluirMoto,
  uploadFotoMoto, listarFotosMoto, getUrlFoto, excluirFotoMoto,
  listarClientes, inserirMovimentacao,
  fmtMoeda, fmtData,
} from '../lib/supabase';
import { Plus, Search, Edit2, Trash2, X, Camera, ChevronDown, ImageOff, FileText, UserPlus } from 'lucide-react';

const STATUS_OPTS = ['Disponível', 'Vendida', 'Reservada', 'Consignada'];
const STATUS_COR  = { 'Disponível': 'verde', 'Vendida': 'vermelho', 'Reservada': 'laranja', 'Consignada': 'azul' };

const FORM_VAZIO = {
  marca: '', modelo: '', ano: '', placa: '', cor: '',
  kmEntrada: '', kmSaida: '', valorCompra: '', valorVenda: '',
  proprietario_id: '', cliente_id: '', dataEntrada: '', dataSaida: '',
  status: 'Disponível', observacao: '',
};

export default function Estoque({ onNavegar }) {
  const [motos,       setMotos]       = useState([]);
  const [clientes,    setClientes]    = useState([]);
  const [busca,       setBusca]       = useState('');
  const [filtroSt,    setFiltroSt]    = useState('Todos');
  const [loading,     setLoading]     = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando,    setEditando]    = useState(null);
  const [form,        setForm]        = useState(FORM_VAZIO);
  const [fotosGaleria,setFotosGaleria]= useState([]);
  const [fotosDoc,    setFotosDoc]    = useState([]);
  const [fotoUrls,    setFotoUrls]    = useState({});
  const [salvando,    setSalvando]    = useState(false);
  const [excluindo,   setExcluindo]   = useState(null);
  const [fotoModal,   setFotoModal]   = useState(null);
  const galeriaRef = useRef();
  const docRef     = useRef();

  const carregar = async () => {
    setLoading(true);
    const [data, cli] = await Promise.all([listarMotos(), listarClientes()]);
    setMotos(data || []);
    setClientes(cli || []);
    const urls = {};
    await Promise.all((data || []).map(async (m) => {
      const fs = await listarFotosMoto(m.id, 'galeria');
      if (fs && fs.length > 0) urls[m.id] = getUrlFoto(fs[0].caminho);
    }));
    setFotoUrls(urls);
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => {
    setEditando(null); setForm(FORM_VAZIO);
    setFotosGaleria([]); setFotosDoc([]);
    setModalAberto(true);
  };

  const abrirEditar = async (m) => {
    setEditando(m.id);
    setForm({
      marca: m.marca || '', modelo: m.modelo || '', ano: m.ano || '',
      placa: m.placa || '', cor: m.cor || '', kmEntrada: m.kmEntrada || '',
      kmSaida: m.kmSaida || '', valorCompra: m.valorCompra || '',
      valorVenda: m.valorVenda || '', proprietario_id: m.proprietario_id || '',
      cliente: m.cliente || '', dataEntrada: m.dataEntrada || '',
      dataSaida: m.dataSaida || '', status: m.status || 'Disponível',
      observacao: m.observacao || '',
    });
    const [fg, fd] = await Promise.all([listarFotosMoto(m.id, 'galeria'), listarFotosMoto(m.id, 'documento')]);
    setFotosGaleria(fg || []); setFotosDoc(fd || []);
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setEditando(null); };

  const handleField = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const salvar = async () => {
    if (!form.marca || !form.modelo) return alert('Marca e Modelo são obrigatórios.');
    if (!editando && fotosDoc.length === 0) return alert('Adicione pelo menos 1 foto do documento da moto.');
    setSalvando(true);
    try {
      const payload = {
        ...form,
        proprietario_id: form.proprietario_id ? parseInt(form.proprietario_id) : null,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
      };
      let motoId = editando;
      if (editando) {
        // Detecta se moto foi marcada como Vendida agora
        const motoAnterior = motos.find(m => m.id === editando);
        const vendidaAgora = form.status === 'Vendida' && motoAnterior?.status !== 'Vendida';
        await atualizarMoto(editando, payload);
        // Lança entrada automática na venda
        if (vendidaAgora && form.valorVenda && parseFloat(form.valorVenda) > 0) {
          const nomeCliente = clientes.find(c => c.id === parseInt(form.cliente_id))?.nome || '';
          await inserirMovimentacao({
            tipo: 'Entrada',
            categoria: 'Venda de Moto',
            descricao: `Venda: ${form.marca} ${form.modelo} ${form.ano || ''}${nomeCliente ? ' → ' + nomeCliente : ''}`.trim(),
            valor: parseFloat(form.valorVenda),
            data: form.dataSaida || new Date().toISOString().slice(0, 10),
            moto_id: motoId,
          });
        }
      } else {
        const nova = await inserirMoto(payload);
        motoId = nova?.id;
        // Lança saída automática no financeiro se tiver valor de compra
        if (motoId && form.valorCompra && parseFloat(form.valorCompra) > 0) {
          await inserirMovimentacao({
            tipo: 'Saída',
            categoria: 'Entrada de Moto',
            descricao: `Compra: ${form.marca} ${form.modelo} ${form.ano || ''}`.trim(),
            valor: parseFloat(form.valorCompra),
            data: form.dataEntrada || new Date().toISOString().slice(0, 10),
            moto_id: motoId,
          });
        }
      }
      if (motoId) {
        for (const f of fotosGaleria) { if (f instanceof File) await uploadFotoMoto(motoId, f, 'galeria'); }
        for (const f of fotosDoc)     { if (f instanceof File) await uploadFotoMoto(motoId, f, 'documento'); }
      }
      await carregar();
      fecharModal();
    } catch (err) {
      alert('Erro ao salvar: ' + (err.message || err));
    } finally { setSalvando(false); }
  };

  const confirmarExcluir = async (id) => {
    if (!window.confirm('Excluir esta moto?')) return;
    setExcluindo(id);
    await excluirMoto(id);
    await carregar();
    setExcluindo(null);
  };

  const addFotos = (tipo, files) => {
    const novos = Array.from(files);
    if (tipo === 'galeria') setFotosGaleria(prev => [...prev, ...novos]);
    else setFotosDoc(prev => [...prev, ...novos]);
  };

  const removerFoto = async (tipo, foto, idx) => {
    if (foto instanceof File) {
      if (tipo === 'galeria') setFotosGaleria(p => p.filter((_, i) => i !== idx));
      else setFotosDoc(p => p.filter((_, i) => i !== idx));
    } else {
      await excluirFotoMoto(foto.id, foto.caminho);
      if (tipo === 'galeria') setFotosGaleria(p => p.filter((_, i) => i !== idx));
      else setFotosDoc(p => p.filter((_, i) => i !== idx));
    }
  };

  const fotoUrl = (f) => f instanceof File ? URL.createObjectURL(f) : getUrlFoto(f.caminho);

  const motosFiltradas = motos.filter(m => {
    const q = busca.toLowerCase();
    const prop = clientes.find(c => c.id === m.proprietario_id);
    const matchBusca = !busca ||
      m.marca?.toLowerCase().includes(q) ||
      m.modelo?.toLowerCase().includes(q) ||
      m.placa?.toLowerCase().includes(q) ||
      prop?.nome?.toLowerCase().includes(q);
    return matchBusca && (filtroSt === 'Todos' || m.status === filtroSt);
  });

  const nomeProprietario = (id) => {
    const c = clientes.find(c => c.id === parseInt(id));
    return c ? c.nome : '';
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Estoque</h1>
          <p className="page-sub">{motos.length} moto{motos.length !== 1 ? 's' : ''} cadastrada{motos.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNovo}><Plus size={18} /> Nova Moto</button>
      </div>

      <div className="filtros-bar">
        <div className="search-wrap">
          <Search size={16} className="search-icon" />
          <input className="search-input" placeholder="Buscar por marca, modelo, placa, proprietário..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <div className="filter-select-wrap">
          <select className="filter-select" value={filtroSt} onChange={e => setFiltroSt(e.target.value)}>
            <option>Todos</option>
            {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown size={14} className="select-arrow" />
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : motosFiltradas.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6h-5l-3 7h11l-1.5-4.5"/></svg>
          <p>{busca || filtroSt !== 'Todos' ? 'Nenhuma moto encontrada.' : 'Nenhuma moto cadastrada ainda.'}</p>
          {!busca && filtroSt === 'Todos' && <button className="btn btn-primary" onClick={abrirNovo}><Plus size={16} /> Adicionar primeira moto</button>}
        </div>
      ) : (
        <div className="motos-grid">
          {motosFiltradas.map(m => (
            <div key={m.id} className="moto-card">
              <div className="moto-foto" onClick={() => abrirEditar(m)} style={{ cursor: 'pointer' }}>
                {fotoUrls[m.id] ? <img src={fotoUrls[m.id]} alt="" /> : <div className="moto-foto-placeholder"><ImageOff size={32} /></div>}
                <span className={`badge badge-${STATUS_COR[m.status] || 'azul'} moto-status`}>{m.status}</span>
              </div>
              <div className="moto-info" onClick={() => abrirEditar(m)} style={{ cursor: 'pointer' }}>
                <h3 className="moto-nome">{m.marca} {m.modelo}</h3>
                <p className="moto-detalhe">{m.ano} · {m.cor} · {m.placa || 'Sem placa'}</p>
                {m.proprietario_id && <p className="moto-prop">🔑 {nomeProprietario(m.proprietario_id)}</p>}
                <div className="moto-valores">
                  <div><span className="valor-label">Compra</span><span className="valor-num entrada">{fmtMoeda(m.valorCompra)}</span></div>
                  <div><span className="valor-label">Venda</span><span className="valor-num saida">{fmtMoeda(m.valorVenda)}</span></div>
                </div>
              </div>
              <div className="moto-acoes">
                <button className="btn btn-ghost btn-sm" onClick={() => abrirEditar(m)}><Edit2 size={15} /></button>
                <button className="btn btn-ghost btn-sm btn-danger" onClick={() => confirmarExcluir(m.id)} disabled={excluindo === m.id}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="modal-backdrop" onClick={fecharModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Moto' : 'Nova Moto'}</h2>
              <button className="modal-close" onClick={fecharModal}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-section-title">Identificação</div>
              <div className="form-grid">
                <div className="form-group"><label className="label">Marca *</label><input className="input-field" name="marca" value={form.marca} onChange={handleField} placeholder="Honda, Yamaha..." /></div>
                <div className="form-group"><label className="label">Modelo *</label><input className="input-field" name="modelo" value={form.modelo} onChange={handleField} placeholder="CG 160, Factor..." /></div>
                <div className="form-group"><label className="label">Ano (Ex: 2010/2011)</label><input className="input-field" name="ano" value={form.ano} onChange={handleField} placeholder="2023/2024" /></div>
                <div className="form-group"><label className="label">Cor</label><input className="input-field" name="cor" value={form.cor} onChange={handleField} placeholder="Vermelha" /></div>
                <div className="form-group"><label className="label">Placa</label><input className="input-field" name="placa" value={form.placa} onChange={handleField} placeholder="ABC-1234" /></div>
                <div className="form-group">
                  <label className="label">Status</label>
                  <div className="select-wrap">
                    <select className="input-field" name="status" value={form.status} onChange={handleField}>
                      {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
                    </select>
                    <ChevronDown size={14} className="select-arrow" />
                  </div>
                </div>
              </div>

              <div className="form-section-title">Proprietário</div>
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="label">Selecionar proprietário (cliente cadastrado)</label>
                  <div className="prop-select-row">
                    <div className="select-wrap" style={{flex:1}}>
                      <select className="input-field" name="proprietario_id" value={form.proprietario_id} onChange={handleField}>
                        <option value="">-- Selecione ou cadastre novo --</option>
                        {clientes.filter(c => c.tipo === 'Proprietário' || !c.tipo).map(c => (
                          <option key={c.id} value={c.id}>{c.nome} {c.cpf ? `· ${c.cpf}` : ''}</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="select-arrow" />
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { fecharModal(); onNavegar && onNavegar('clientes'); }}>
                      <UserPlus size={15} /> Novo cliente
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-section-title">Origem & Destino</div>
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="label">Cliente (comprador)</label>
                  <div className="prop-select-row">
                    <div className="select-wrap" style={{flex:1}}>
                      <select className="input-field" name="cliente_id" value={form.cliente_id} onChange={handleField}>
                        <option value="">-- Selecione ou cadastre novo --</option>
                        {clientes.map(c => (
                          <option key={c.id} value={c.id}>{c.nome} {c.cpf ? `· ${c.cpf}` : ''}</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="select-arrow" />
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { fecharModal(); onNavegar && onNavegar('clientes'); }}>
                      <UserPlus size={15} /> Novo cliente
                    </button>
                  </div>
                </div>
                <div className="form-group"><label className="label">Data Entrada</label><input className="input-field" name="dataEntrada" type="date" value={form.dataEntrada} onChange={handleField} /></div>
                <div className="form-group"><label className="label">Data Saída</label><input className="input-field" name="dataSaida" type="date" value={form.dataSaida} onChange={handleField} /></div>
              </div>

              <div className="form-section-title">Quilometragem & Valores</div>
              <div className="form-grid">
                <div className="form-group"><label className="label">KM Entrada</label><input className="input-field" name="kmEntrada" type="number" value={form.kmEntrada} onChange={handleField} placeholder="0" /></div>
                <div className="form-group"><label className="label">KM Saída</label><input className="input-field" name="kmSaida" type="number" value={form.kmSaida} onChange={handleField} placeholder="0" /></div>
                <div className="form-group"><label className="label">Valor de Compra</label><input className="input-field" name="valorCompra" type="number" step="0.01" value={form.valorCompra} onChange={handleField} placeholder="0,00" /></div>
                <div className="form-group"><label className="label">Valor de Venda</label><input className="input-field" name="valorVenda" type="number" step="0.01" value={form.valorVenda} onChange={handleField} placeholder="0,00" /></div>
              </div>

              <div className="form-section-title">Observação</div>
              <textarea className="input-field textarea" name="observacao" value={form.observacao} onChange={handleField} placeholder="Detalhes adicionais..." rows={3} />

              <div className="form-section-title">📷 Fotos da Moto</div>
              <FotoGrid fotos={fotosGaleria} onAdd={files => addFotos('galeria', files)} onRemove={(f,i) => removerFoto('galeria',f,i)} onView={setFotoModal} inputRef={galeriaRef} />

              <div className="form-section-title">📄 Documento da Moto <span className="obrigatorio">*obrigatório</span></div>
              <FotoGrid fotos={fotosDoc} onAdd={files => addFotos('documento', files)} onRemove={(f,i) => removerFoto('documento',f,i)} onView={setFotoModal} inputRef={docRef} icone={<FileText size={20} />} label="Adicionar doc" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={fecharModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar moto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {fotoModal && <div className="modal-backdrop" onClick={() => setFotoModal(null)}><img src={fotoModal} alt="" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12}} /></div>}

      <style>{`
        .page { padding: 24px; }
        .page-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
        .page-title { font-size:1.5rem; font-weight:700; color:var(--azul); margin:0; }
        .page-sub { color:#6B7280; font-size:.875rem; margin:2px 0 0; }
        .filtros-bar { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
        .search-wrap { position:relative; flex:1; min-width:200px; }
        .search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#9CA3AF; }
        .search-input { width:100%; padding:9px 12px 9px 36px; border:1.5px solid #E5E7EB; border-radius:8px; font-size:.9rem; outline:none; transition:.15s; box-sizing:border-box; }
        .search-input:focus { border-color:var(--azul); }
        .filter-select-wrap { position:relative; }
        .filter-select { padding:9px 32px 9px 14px; border:1.5px solid #E5E7EB; border-radius:8px; font-size:.9rem; outline:none; appearance:none; background:#fff; cursor:pointer; }
        .select-arrow { position:absolute; right:10px; top:50%; transform:translateY(-50%); pointer-events:none; color:#6B7280; }
        .select-wrap { position:relative; }
        .motos-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:16px; }
        .moto-card { background:#fff; border-radius:12px; border:1.5px solid #E5E7EB; overflow:hidden; transition:.15s; }
        .moto-card:hover { border-color:#CBD5E1; box-shadow:0 4px 16px rgba(0,0,0,.08); }
        .moto-foto { position:relative; height:180px; background:#F1F5F9; }
        .moto-foto img { width:100%; height:100%; object-fit:cover; }
        .moto-foto-placeholder { display:flex; align-items:center; justify-content:center; height:100%; color:#CBD5E1; }
        .moto-status { position:absolute; top:10px; right:10px; }
        .moto-info { padding:14px 16px 8px; }
        .moto-nome { font-size:1rem; font-weight:700; color:var(--azul); margin:0 0 4px; }
        .moto-detalhe { font-size:.82rem; color:#6B7280; margin:0 0 4px; }
        .moto-prop { font-size:.82rem; color:#6B7280; margin:0 0 8px; }
        .moto-valores { display:flex; gap:16px; }
        .moto-valores > div { display:flex; flex-direction:column; }
        .valor-label { font-size:.72rem; color:#9CA3AF; text-transform:uppercase; letter-spacing:.04em; }
        .valor-num { font-size:.88rem; font-weight:600; }
        .valor-num.entrada { color:#22C55E; }
        .valor-num.saida { color:var(--azul); }
        .moto-acoes { display:flex; justify-content:flex-end; gap:4px; padding:8px 12px 12px; }
        .btn-ghost { background:transparent; border:1.5px solid #E5E7EB; color:#6B7280; }
        .btn-ghost:hover { background:#F9FAFB; }
        .btn-danger:hover { color:#EF4444 !important; border-color:#EF4444 !important; }
        .modal-lg { width:100%; max-width:720px; }
        .modal-header { display:flex; justify-content:space-between; align-items:center; padding:20px 24px 0; }
        .modal-header h2 { font-size:1.1rem; font-weight:700; color:var(--azul); }
        .modal-close { background:none; border:none; cursor:pointer; color:#9CA3AF; }
        .modal-body { padding:20px 24px; max-height:70vh; overflow-y:auto; }
        .modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #F1F5F9; }
        .form-section-title { font-size:.75rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#9CA3AF; margin:16px 0 10px; }
        .form-section-title:first-child { margin-top:0; }
        .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .form-group { display:flex; flex-direction:column; gap:4px; }
        .form-full { grid-column:1/-1; }
        .textarea { resize:vertical; min-height:72px; }
        .prop-select-row { display:flex; gap:8px; align-items:flex-start; }
        .obrigatorio { color:#EF4444; font-size:.7rem; font-weight:700; margin-left:6px; text-transform:none; }
        @media(max-width:640px){ .page{padding:16px;} .motos-grid{grid-template-columns:1fr;} .form-grid{grid-template-columns:1fr;} }
      `}</style>
    </div>
  );
}

function FotoGrid({ fotos, onAdd, onRemove, onView, inputRef, icone, label }) {
  return (
    <div className="fotos-grid">
      {fotos.map((f, i) => {
        const url = f instanceof File ? URL.createObjectURL(f) : getUrlFoto(f.caminho);
        return (
          <div key={i} className="foto-thumb">
            <img src={url} alt="" onClick={() => onView(url)} />
            <button className="foto-remove" onClick={() => onRemove(f, i)}><X size={12} /></button>
          </div>
        );
      })}
      <button className="foto-add" onClick={() => inputRef.current?.click()}>
        {icone || <Camera size={22} />}
        <span>{label || 'Adicionar'}</span>
      </button>
      <input ref={inputRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={e => onAdd(e.target.files)} />
      <style>{`
        .fotos-grid { display:flex; flex-wrap:wrap; gap:10px; margin-top:4px; }
        .foto-thumb { position:relative; width:88px; height:88px; border-radius:8px; overflow:hidden; border:1.5px solid #E5E7EB; }
        .foto-thumb img { width:100%; height:100%; object-fit:cover; cursor:pointer; }
        .foto-remove { position:absolute; top:4px; right:4px; background:rgba(0,0,0,.6); color:#fff; border:none; border-radius:50%; width:20px; height:20px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
        .foto-add { width:88px; height:88px; border-radius:8px; border:1.5px dashed #CBD5E1; background:#F8FAFC; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; cursor:pointer; color:#94A3B8; font-size:.72rem; font-weight:500; }
        .foto-add:hover { border-color:var(--azul); color:var(--azul); }
      `}</style>
    </div>
  );
}
